import { type ReactNode, createContext, useContext, useEffect } from "react";
import type { WeekNav } from "../kit/WeekNav.js";
import type { Me } from "../api/client.js";
import type { WeekModel } from "./model/week.js";

export type LiftSort = "heaviest" | "improved" | "name";

/** What the views remember between pages while the portal is open. */
export interface PortalUI {
  planColumn: string | null;
  lift: { [athleteID: string]: string };
  liftQuery: string;
  liftColumn: string | null;
  liftSort: LiftSort;
  athletesExpanded: boolean;
}

export interface PortalContextValue {
  me: Me;
  /** After they change who they are (Settings). */
  setMe: (me: Me) => void;
  week: string;
  model: WeekModel | null;
  ui: PortalUI;
  setUI: (change: Partial<PortalUI>) => void;
  pickLift: (athleteID: string, key: string) => void;
  sideBySide: boolean;
  /** Opens the sheet beside the page; returns false when the window is too narrow, so the link opens a tab. */
  openSheet: (week: string, toggle?: boolean) => boolean;
  announce: (text: string) => void;
  /** The week's ‹ › and picker for a week page's header (DESIGN §16.6): they keep the page. */
  weekNav: WeekNav;
  /** The Coach's sheet button for the shown week, when it has a sheet. */
  sheetButton: ReactNode;
}

export const PortalContext = createContext<PortalContextValue | null>(null);

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext);
  if (!value) throw new Error("usePortal outside the portal");
  return value;
}

export function useModel(): WeekModel {
  const { model } = usePortal();
  if (!model) throw new Error("The week is not loaded");
  return model;
}

export const AnnounceContext = createContext<(text: string) => void>(() => {});

export function useTitle(title: string): void {
  useEffect(() => {
    document.title = title + " · Training Coach Portal";
  }, [title]);
}
